import React from 'react';
import c from 'classnames';

import { SearchResults } from '@uncinc/react-searchkit';
import type { Results } from '@uncinc/react-searchkit/types';

import { Image } from '@uncinc/uncinc-react-kitchen-sink';

import { ReactComponent as Avatar } from '../../../assets/images/avatar.svg';
import constructVariableRatiosImageSourcesObject from '../../../helpers/constructImageSources';
import type { SearchAllContentHit } from '../types';
import PageHit, { type SearchAllContentPageHitProps } from './PageHit';

import './style.scss';

// If content type is "partner", "annual report" or "vacancy", we want to use the link_url field instead of the default url,
// since these do not have a detail page.
function getNodeLink(hit: SearchAllContentHit) {
  if ('content_type' in hit) {
    switch (hit.content_type[0]) {
      case 'partner':
      case 'annual_report':
      case 'vacancy': {
        return 'link_url' in hit ? hit.link_url?.[0] : undefined;
      }

      default: {
        return hit.url[0];
      }
    }
  }

  return hit.url[0];
}

function getRenderItemProps(hit: SearchAllContentHit): SearchAllContentPageHitProps {
  const image = constructVariableRatiosImageSourcesObject(
    hit,
    ['1_1', '1_1', '4_3', '4_3', '4_3'],
    ['xxs', 'xxs', 'xxs', 'xxs', 'xxs'],
  );

  if ('vid' in hit) {
    return {
      contentType: hit.vid[0],
      link: hit.url[0],
      title: hit.taxonomy_name[0],
      subtitle: hit.taxonomy_description?.[0],
      image: image && (
        <Image
          src={image}
          alt={hit.taxonomy_image_alt?.[0]}
          lazyload={false}
        />
      ),
    };
  }

  if ('profile_type' in hit) {
    return {
      contentType: hit.profile_type[0],
      link: hit.url[0],
      title: `${hit.first_name[0]} ${hit.last_name[0]}`,
      subtitle: hit.profile_description_processed?.[0],
      image: image ? (
        <Image
          src={image}
          alt={hit.profile_image_alt?.[0]}
          lazyload={false}
        />
      ) : (
        <Avatar />
      ),
    };
  }

  return {
    contentType: hit.content_type[0],
    articleType: 'article_type_name' in hit ? hit.article_type_name[0] : undefined,
    link: getNodeLink(hit),
    title: hit.page_title[0],
    subtitle: hit.body_summarized?.[0] || hit.body?.[0] || hit.paragraphs_body_processed?.[0],
    hideTag:
      hit.content_type[0] === 'basic_page' ||
      hit.content_type[0] === 'landing_page' ||
      hit.content_type[0] === 'overview_page' ||
      hit.content_type[0] === 'homepage',
    image: image && (
      <Image
        src={image}
        alt=""
        lazyload={false}
      />
    ),
  };
}

const RenderItem = (hit: SearchAllContentHit) => {
  const renderItemProps = getRenderItemProps(hit);

  return (
    <PageHit {...renderItemProps} />
  );
};

const SearchAllContentResults: React.FC<Omit<Results, 'renderItem'>> = (props) => {
  const { className } = props;

  const classNames = c('SearchAllContent__Results', className);

  return (
    <SearchResults
      className={classNames}
      renderItem={RenderItem}
    />
  );
};

export default SearchAllContentResults;
